import { rem } from 'polished'
import styled, { css } from 'styled-components'

interface Props {
  /**
   * `fit-month` trims each day to what its content actually needs, so a
   * six-week month — the tallest there is — fits a search panel without
   * anyone having to scroll for its last day. Booked-item chips are dropped
   * with it: a day carrying two of them is taller than the budget allows, and
   * a search panel is for choosing dates rather than reading the itinerary.
   * The editor keeps the roomier default, where a day holds chips as well.
   */
  $density?: 'default' | 'fit-month'
}

/**
 * The frame every trip planner calendar sits in.
 *
 * Holds what the shared calendar does not: the trip planner's own selection
 * colours, a weekday row that survives scrolling, and the option to tighten a
 * day down to what a search panel has room for.
 */
const TripCalendarShell = styled.div<Props>`
  min-width: 0;

  ${props => props.$density === 'fit-month' && css`
    [data-calendar-date] {
      min-height: ${rem(46)};
      padding-block: ${rem(6)};
    }

    /* Chips would blow the six-week height budget this density promises. */
    [data-calendar-date] [data-stop-day-chips] {
      display: none;
    }
  `}

  /*
   * A selected day is already the darkest tile on the grid, so the ring around
   * it only adds noise. Keyboard focus keeps its own ring — that one is the
   * only cue a keyboard user has, so it is deliberately left alone.
   */
  [data-calendar-date].state-range-start:not(:focus-visible),
  [data-calendar-date].state-range-end:not(:focus-visible),
  [data-calendar-date].state-preview-end:not(:focus-visible),
  [data-calendar-date].state-selected:not(:focus-visible) {
    outline: none;
  }

  /*
   * The calendar declares these on its own root, so the override has to beat a
   * single class — hence the doubled selector rather than a plain child rule.
   */
  && > * {
    /* The chosen check-in and check-out. */
    --lux-cal-range-edge: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
    /* Every night in between. */
    --lux-cal-range-fill: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  /*
   * One weekday row sits above every month the calendar renders, so it
   * scrolls away with the first of them. Pinning it keeps Mon–Sun readable
   * while the months run past underneath.
   *
   * Selected structurally because it is the calendar root's only aria-hidden
   * child — the row carries no accessible name of its own to match on.
   */
  > * > [aria-hidden='true'] {
    position: sticky;
    top: 0;
    z-index: 1;
    padding-block: ${rem(8)};
    background: var(--lux-cal-surface);
  }
`

export default TripCalendarShell
